/**
 * Customer detail sub-components — ported from AJS directives.
 *
 * branch-invoices              → wor-table backed by /customers/branch_invoice_list
 * customer-branch-payments     → wor-table backed by /customers/branch_payment_list
 * customer-branch-store-payments → wor-table backed by /customers/branch_payment_list
 */
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UIRouter }         from '@uirouter/core';

import { ApiService }           from '../api/api.service';
import { CustomerModel }        from './models/customer';
import { TableQueryParameters } from '../table/types/table-query-parameters';
import { TableRowEvent }        from '../table/types/table-row-event';
import { TableService }         from '../table/table.service';
import { EditComponent }        from '../table/cell-components/actions/edit.component';

// ---------------------------------------------------------------------------
// branch-invoices
// ---------------------------------------------------------------------------
@Component({
  selector: 'branch-invoices',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-folder-open"></i><span class="break"></span>{{ 'Branch Invoices' }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="invoices"
          (query)="getInvoices($event.params)"
          [settings]="tableSettings">
        </wor-table>
      </div>
    </div>
  ` })
export class BranchInvoicesPlaceholderComponent implements OnInit {
  @Input() customer: CustomerModel;
  @Input() invoices: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.invoices?.length;

    this.tableSettings = {
      columns: [
        { field: 'invoice_number', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.REFERENCE') }, classes: 'text-left' },
        { field: 'post_date',      header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE'), classes: 'text-left' }, pipe: 'globalDate', classes: 'text-left' },
        { field: 'invoice_type',   header: { text: this.translate.instant('JS_SPACE.COLUMNS.TRAN_TYPE'), classes: 'text-left' }, pipe: 'invoiceType', classes: 'text-left' },
        { field: 'invoice_status', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS'), classes: 'text-left' }, pipe: 'invoiceStatus', classes: 'text-left' },
        { field: 'store_name',     header: { text: 'Store Name', classes: 'text-left' }, classes: 'text-left' },
        { field: 'total',          header: { text: this.translate.instant('GENERAL_SPACE.FIELD.TOTAL'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { field: 'balance_due',    header: { text: this.translate.instant('JS_SPACE.COLUMNS.BALANCE'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_INVOICES_FOUND') },
      pagination: {
        enabled:   true,
        pageSize:  5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/customers/branch_invoice_count', [this.customer.id, '*', '*', '*', '*', '*']) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: 'desc', field: 'post_date' } };
  }

  getInvoices(params: TableQueryParameters) {
    return this.api.get('/customers/branch_invoice_list', [this.customer.id, params.offset, params.pageSize, '*', '*', params.field, params.direction])
      .then((invoices: Record<string, unknown>[]) => { this.invoices = invoices; return invoices; });
  }
}

// ---------------------------------------------------------------------------
// customer-branch-payments
// ---------------------------------------------------------------------------
@Component({
  selector: 'customer-branch-payments',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-money"></i><span class="break"></span>{{ 'Branch Payments' }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="payments"
          (query)="getPayments($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class CustomerBranchPaymentsPlaceholderComponent implements OnInit {
  @Input() customer: CustomerModel;
  @Input() payments: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.payments?.length;
    const cache = this.tableService.getCacheForTable('customerBranchPayments');

    this.tableSettings = {
      cache: { enabled: true, key: 'customerBranchPayments' },
      columns: [
        { field: 'branch_customer_payment_number', header: { text: this.translate.instant('JS_SPACE.COLUMNS.PAYMENT_NUMBER') } },
        { field: 'post_date',                      header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'status',                         header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'customerPaymentStatus' },
        { field: 'applied_amount',                 header: { text: this.translate.instant('JS_SPACE.COLUMNS.APPLIED_AMOUNT'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { field: 'amount',                         header: { text: this.translate.instant('JS_SPACE.COLUMNS.AMOUNT'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { component: { bindings: { outputs: { onEditClick: (e: { id: string; customer_id: string }) => this.viewPayment(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: 'No Branch Payments Found' },
      pagination: {
        enabled:   true,
        pageSize:  cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/branch_customer_payments/count') },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'post_date' } };
  }

  getPayments(params: TableQueryParameters) {
    return this.api.get('/customers/branch_payment_list', [this.customer.id, params.offset, params.pageSize, params.field, params.direction])
      .then((payments: Record<string, unknown>[]) => { this.payments = payments; return payments; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewPayment(event.row);
  }

  viewPayment(payment: { id: string; customer_id: string }) {
    this.router.stateService.go('app.customer-branch-payment', { customer_id: this.customer.id, id: payment.id });
  }
}

// ---------------------------------------------------------------------------
// customer-branch-store-payments
// ---------------------------------------------------------------------------
@Component({
  selector: 'customer-branch-store-payments',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-money"></i><span class="break"></span>{{ 'Branch Store Payments' }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="payments"
          (query)="getPayments($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class CustomerBranchStorePaymentsPlaceholderComponent implements OnInit {
  @Input() customer: CustomerModel;
  @Input() payments: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.payments?.length;

    this.tableSettings = {
      columns: [
        { field: 'customer_payment_number', header: { text: this.translate.instant('JS_SPACE.COLUMNS.PAYMENT_NUMBER') } },
        { field: 'store_name',              header: { text: 'Store Name' } },
        { field: 'post_date',               header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'status',                  header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'customerPaymentStatus' },
        { field: 'applied_amount',          header: { text: this.translate.instant('JS_SPACE.COLUMNS.APPLIED_AMOUNT'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { field: 'amount',                  header: { text: this.translate.instant('JS_SPACE.COLUMNS.AMOUNT'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { component: { bindings: { outputs: { onEditClick: (e: { id: string; customer_id: string }) => this.viewPayment(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_PAYMENTS_FOUND') },
      pagination: {
        enabled:   true,
        pageSize:  5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/branch_customer_payments/count') },
      rows: { highlight: { enabled: true } },
      sorting: { direction: 'desc', field: 'post_date' } };
  }

  getPayments(params: TableQueryParameters) {
    return this.api.get('/customers/branch_payment_list', [this.customer.id, params.offset, params.pageSize, params.field, params.direction])
      .then((payments: Record<string, unknown>[]) => { this.payments = payments; return payments; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewPayment(event.row);
  }

  viewPayment(payment: { id: string; customer_id: string }) {
    this.router.stateService.go('app.customer-branch-payment', { customer_id: this.customer.id, id: payment.id });
  }
}
